import { useCallback, useRef, useEffect } from '@wordpress/element';

/**
 * useWpMedia
 *
 * React hook that opens the native WordPress Media Library picker.
 * Requires wp_enqueue_media() to be called in PHP before the page loads.
 *
 * The internal wp.media frame is created once and reused on every open()
 * call. The onSelect callback is stored in a ref so it always reflects the
 * latest version without needing to recreate the frame.
 *
 * @param {object}   options
 * @param {string}   [options.title="Select Image"]        Modal title
 * @param {string}   [options.buttonText="Use this image"] Button label
 * @param {string}   [options.type="image"]                MIME type filter
 * @param {function} options.onSelect  Called with { id, url, filename, alt, width, height, mime }
 *
 * @returns {{ open: function }} Call open() to launch the picker
 */
export const useWpMedia = ({
    title      = 'Select Image',
    buttonText = 'Use this image',
    type       = 'image',
    onSelect,
} = {}) => {
    const frameRef    = useRef(null);
    const onSelectRef = useRef(onSelect); // always holds the latest callback

    // Keep ref current without recreating the frame on every render
    useEffect(() => {
        onSelectRef.current = onSelect;
    }, [onSelect]);

    const open = useCallback(() => {
        if (typeof window?.wp?.media === 'undefined') {
            console.error(
                '[useWpMedia] wp.media is not available.\n' +
                'Make sure wp_enqueue_media() is called in PHP before this page loads.'
            );
            return;
        }

        // Create the wp.media frame only once
        if (!frameRef.current) {
            frameRef.current = window.wp.media({
                title,
                library: { type },
                button:  { text: buttonText },
                multiple: false,
            });

            // Use onSelectRef so this handler is never stale
            frameRef.current.on('select', () => {
                const attachment = frameRef.current
                    .state()
                    .get('selection')
                    .first()
                    .toJSON();

                onSelectRef.current?.({
                    id:       attachment.id,
                    url:      attachment.url,
                    filename: attachment.filename ?? attachment.name ?? '',
                    alt:      attachment.alt ?? '',
                    width:    attachment.width,
                    height:   attachment.height,
                    mime:     attachment.mime,
                });
            });
        }

        // Always open to the library tab (not "Insert from URL")
        frameRef.current.setState('library');
        frameRef.current.open();

    }, []); // empty deps — frame config is fixed at mount time

    return { open };
};
